import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import client from '../../api/client';
import { pushToast } from '../ui/toastBus';

// `kind` is the API resource: 'candidates' or 'leads'. Photos sit behind auth, so they are
// fetched as a blob (an <img src> can't send the bearer token) and shown via an object URL.
export default function ProfilePhoto({ kind, id, hasPhoto, name, editable, queryKey, size = 'h-20 w-16' }) {
  const inputRef = useRef(null);
  const queryClient = useQueryClient();
  const [url, setUrl] = useState(null);

  const photoQuery = useQuery({
    queryKey: [kind, String(id), 'photo'],
    queryFn: async () => (await client.get(`/${kind}/${id}/photo`, { responseType: 'blob' })).data,
    enabled: !!hasPhoto,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!photoQuery.data) {
      setUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(photoQuery.data);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [photoQuery.data]);

  const upload = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('photo', file);
      await client.post(`/${kind}/${id}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [kind, String(id), 'photo'] });
      queryClient.invalidateQueries({ queryKey: queryKey || [kind, String(id)] });
      pushToast({ type: 'success', message: 'Profile photo updated.' });
    },
    onError: (err) =>
      pushToast({ type: 'error', message: err.response?.data?.errors?.photo?.[0] || 'Could not upload photo.' }),
  });

  const onFile = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (file) upload.mutate(file);
  };

  const body = url ? (
    <img src={url} alt={name || 'Profile'} className="h-full w-full object-cover" />
  ) : (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-3/5 w-3/5 text-gray-400" aria-hidden="true">
      <path d="M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Zm0 2c-4.14 0-7.5 2.24-7.5 5v1.5h15V19c0-2.76-3.36-5-7.5-5Z" />
    </svg>
  );

  const frame = `relative flex ${size} shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-100`;

  if (!editable) return <div className={frame}>{body}</div>;

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={upload.isPending}
        title={url ? 'Change profile photo' : 'Upload profile photo'}
        className={`group ${frame} focus:outline-none focus:ring-2 focus:ring-primary`}
      >
        {body}
        <span className="absolute inset-0 flex items-center justify-center bg-black/50 px-1 text-center text-[10px] font-medium leading-tight text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100">
          {upload.isPending ? 'Uploading…' : url ? 'Change' : 'Upload photo'}
        </span>
      </button>
      <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.heic,.heif" onChange={onFile} className="hidden" />
    </>
  );
}
